import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldAlert, KeyRound, Send, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Spinner } from "../components/common";

/* Spec §17 — Ownership Transfer (owner-initiated).
   Requires the caller to re-authenticate in the last 10 minutes.
*/
export default function OwnershipTransfer() {
  const { user, logout } = useAuth() || {};
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [adult, setAdult] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/ownership/status").then((r) => setStatus(r.data)).catch(() => setStatus({ outgoing: [], incoming: [] }));
  }, [user]);

  if (!user) return (
    <div className="max-w-lg mx-auto p-8 text-center">
      Please <Link className="text-blue-700 underline" to="/login">sign in</Link> first.
    </div>
  );

  const doReauth = async () => {
    toast.info("Signing you out — please sign back in and return here to continue.");
    try { if (typeof logout === "function") await logout(); }
    catch { /* noop */ }
    setTimeout(() => nav(`/login?next=/settings/ownership-transfer`), 900);
  };

  const initiate = async (e) => {
    e.preventDefault();
    if (!adult) { toast.error("Confirm both parties are adults over 18"); return; }
    if (!/^\S+@\S+\.\S+$/.test(email)) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      const r = await api.post("/ownership/initiate", {
        recipient_email: email.trim().toLowerCase(),
        confirm_adult: true,
        note: note.trim() || undefined,
      });
      toast.success(`Invitation sent to ${r.data.recipient_email}`);
      setEmail(""); setNote(""); setAdult(false);
      const s = await api.get("/ownership/status");
      setStatus(s.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not start the transfer";
      if (err?.response?.status === 401) {
        toast.error("Please sign in again — an ownership transfer requires a fresh login.");
        await doReauth();
      } else toast.error(msg);
    } finally { setBusy(false); }
  };

  const cancel = async (tid) => {
    try {
      await api.post(`/ownership/cancel/${tid}`);
      toast.success("Transfer cancelled");
      const s = await api.get("/ownership/status");
      setStatus(s.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); }
  };

  if (!status) return <Spinner />;

  return (
    <div className="max-w-2xl mx-auto px-5 py-8" data-testid="ownership-transfer-page">
      <Link to="/settings/privacy" className="inline-flex items-center gap-1 text-blue-700 text-sm hover:underline mb-3">
        <ArrowLeft className="w-4 h-4" /> Settings
      </Link>
      <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-rose-600 flex items-center gap-1.5">
        <ShieldAlert className="w-3.5 h-3.5" /> Spec §17 · Owner-Only
      </div>
      <h1 className="font-serif font-800 text-4xl text-slate-900 mt-1">Transfer ownership</h1>
      <p className="text-slate-600 mt-2 text-sm max-w-lg">
        Move your entire TravelerPulse Hub — trips, travelers, memberships, loyalty records,
        Inner Circle and vault items — to another adult's account. Both parties must be over 18
        and must re-authenticate before the transfer can complete.
      </p>

      <div className="mt-6 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="ownership-warning">
        <b>This cannot be undone.</b> After acceptance, all your trips and workspace items will
        belong to the new owner. Your sign-in still works, but you'll no longer own those items.
      </div>

      {/* Outgoing pending */}
      {status.outgoing?.length > 0 && (
        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="ownership-outgoing">
          <div className="text-[10px] font-800 uppercase tracking-widest text-slate-500 mb-2">Pending — waiting on recipient</div>
          {status.outgoing.map((o) => (
            <div key={o.id} className="flex items-center justify-between border border-slate-200 rounded-xl px-3 py-2.5 mb-2 last:mb-0">
              <div>
                <div className="text-sm font-700 text-slate-900">{o.recipient_email}</div>
                <div className="text-xs text-slate-500">Sent {(o.created_at || "").slice(0, 16).replace("T", " ")} · Expires {(o.expires_at || "").slice(0, 10)}</div>
              </div>
              <button
                onClick={() => cancel(o.id)}
                data-testid={`ownership-cancel-${o.id}`}
                className="inline-flex items-center gap-1.5 border border-rose-300 text-rose-700 text-xs font-800 px-2.5 py-1.5 hover:bg-rose-50">
                <XCircle className="w-3.5 h-3.5" /> Cancel
              </button>
            </div>
          ))}
        </section>
      )}

      {/* Initiate form */}
      <form onSubmit={initiate} className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="ownership-initiate-form">
        <div className="text-[10px] font-800 uppercase tracking-widest text-slate-500 mb-3">Start a transfer</div>

        <button
          type="button"
          onClick={doReauth}
          data-testid="ownership-reauth-btn"
          className="mb-4 inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 px-3 py-2 text-xs font-700 hover:bg-slate-50">
          <KeyRound className="w-4 h-4" /> Re-authenticate (required in the last 10 min)
        </button>

        <label className="block text-xs font-700 text-slate-700 mb-1">Recipient email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="ownership-recipient-input"
          placeholder="new-owner@example.com"
          className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm mb-3 focus:outline-none focus:border-rose-500"
        />

        <label className="block text-xs font-700 text-slate-700 mb-1">Personal note (optional)</label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="ownership-note-input"
          maxLength={400}
          rows={3}
          placeholder="A short message the recipient will see in the invitation email…"
          className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm mb-3 focus:outline-none focus:border-rose-500"
        />

        <label className="flex items-start gap-2 text-sm text-slate-800 mb-3">
          <input
            type="checkbox"
            checked={adult}
            onChange={(e) => setAdult(e.target.checked)}
            data-testid="ownership-adult-checkbox"
            className="mt-1"
          />
          <span>I confirm both my account and the recipient's account belong to adults over 18.</span>
        </label>

        <button
          type="submit"
          disabled={busy || !email || !adult}
          data-testid="ownership-initiate-btn"
          className="inline-flex items-center gap-2 bg-rose-600 text-white font-800 px-4 py-2.5 text-sm hover:bg-rose-700 disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send transfer invitation
        </button>
      </form>

      {/* Incoming — if this user is a recipient */}
      {status.incoming?.length > 0 && (
        <section className="mt-6 rounded-2xl border border-emerald-300 bg-emerald-50 p-5" data-testid="ownership-incoming">
          <div className="text-[10px] font-800 uppercase tracking-widest text-emerald-700 mb-2">Someone wants to transfer their hub to you</div>
          {status.incoming.map((i) => (
            <div key={i.id} className="flex items-center justify-between mb-2 last:mb-0">
              <div>
                <div className="text-sm font-800 text-emerald-900">{i.owner_email}</div>
                <div className="text-xs text-emerald-800/80">Expires {(i.expires_at || "").slice(0, 10)}</div>
              </div>
              <button
                onClick={() => nav("/settings/ownership-transfer/accept")}
                data-testid={`ownership-open-accept-${i.id}`}
                className="text-xs font-800 bg-emerald-600 text-white px-3 py-2 hover:bg-emerald-700">
                Open the invitation
              </button>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
